import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError, BaseResponseEntities } from 'app/shared/reducers/reducer.utils';
import { IWorkGroup, defaultValue } from 'app/shared/model/work-group.model';
import { apiOkr } from 'app/config/axios-interceptor';

const initialState: EntityState<IWorkGroup> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/okr/work-type';

// Actions

export const getEntities = createAsyncThunk(
  'workGroup/fetch_entity_list',
  async ({ searchText, page, size, sort, filters }: IQueryParams) => {
    return apiOkr.get<BaseResponseEntities<IWorkGroup>>(`${apiUrl}/pagination`, {
      params: {
        searchText,
        PageNum: page,
        pageSize: size,
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getEntity = createAsyncThunk(
  'workGroup/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return apiOkr.get<IWorkGroup>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'workGroup/create_entity',
  async (entity: IWorkGroup, thunkAPI) => {
    const result = await apiOkr.post<IWorkGroup>(apiUrl, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'workGroup/update_entity',
  async (entity: IWorkGroup, thunkAPI) => {
    const result = await apiOkr.put<IWorkGroup>(`${apiUrl}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const partialUpdateEntity = createAsyncThunk(
  'workGroup/partial_update_entity',
  async (entity: IWorkGroup, thunkAPI) => {
    const result = await apiOkr.patch<IWorkGroup>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
  'workGroup/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await apiOkr.delete<IWorkGroup>(requestUrl);
    return result;
  },
  { serializeError: serializeAxiosError },
);

// slice

export const WorkGroupSlice = createEntitySlice({
  name: 'workGroup',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data.items,
          totalItems: data.totalRecord,
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = WorkGroupSlice.actions;

// Reducer
export default WorkGroupSlice.reducer;
